import React, { ReactElement } from 'react';

import { EBool } from '~/src/dash/api/types/bool';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { Col, Cols, IndependentSelectV2, Row, Rows, spacing, Text } from '~/src/dash/publicaUI';

const buildOptions = (values: string[], label?: string) => {
  return values.map((v) => {
    let text = v;
    if (label) {
      text += ' ' + label;
    }
    return {
      id: v,
      label: text,
    } as ISelectOption;
  });
};

const VideoResolutions = ['1920x1080', '1280x720', '640x480', '480x360'];
const VideoBitrates = ['15000', '12000', '7500', '6000', '4000', '3500', '2500', '1500'];
const VideoFormats = ['mp4'];
const FrameRates = ['23.98', '24', '25', '29.97', '30'];
const AudioBitrates = ['192', '128', '320'];
const SamplingFrequencies = ['48', '44.1', '96', '88.2'];

const VideoResolutionOptions = buildOptions(VideoResolutions);
const VideoBitrateOptions = buildOptions(VideoBitrates, 'kbps');
const VideoFormatOptions = buildOptions(VideoFormats);
const FrameRateOptions = buildOptions(FrameRates, 'fps');
const CodecOptions = [
  {
    id: 'h.264_high',
    label: 'H.264 High',
  },
  {
    id: 'h.264_main',
    label: 'H.264 Main',
  },
  {
    id: 'h.264_baseline',
    label: 'H.264 Baseline',
  },
];
const AudioBitrateOptions = buildOptions(AudioBitrates, 'kbps');
const SamplingFrequencyOptions = buildOptions(SamplingFrequencies, 'kHz');

interface ICreativeQualityAssuranceView {
  enable: EBool;
  videoResolution: string;
  handleChangeValue: (name: string, value: string) => void;
  videoBitrate: string;
  videoFormat: string;
  frameRate: string;
  codec: string;
  audioBitrate: string;
  samplingFrequency: string;
}

export const CreativeQualityAssuranceView = ({
  enable,
  videoResolution,
  videoBitrate,
  videoFormat,
  frameRate,
  codec,
  audioBitrate,
  samplingFrequency,
  handleChangeValue,
}: ICreativeQualityAssuranceView): ReactElement => {
  const cqaEnabled = enable === EBool.EBool_TRUE;
  if (!videoResolution) {
    videoResolution = VideoResolutionOptions[0].id as string;
    handleChangeValue('CreativeQualityAssuranceVideoResolution', videoResolution);
  }
  if (!videoBitrate) {
    videoBitrate = VideoBitrateOptions[0].id as string;
    handleChangeValue('CreativeQualityAssuranceVideoBitrate', videoBitrate);
  }
  if (!videoFormat) {
    videoFormat = VideoFormatOptions[0].id as string;
    handleChangeValue('CreativeQualityAssuranceVideoFormat', videoFormat);
  }
  if (!frameRate) {
    frameRate = FrameRateOptions[0].id as string;
    handleChangeValue('CreativeQualityAssuranceFrameRate', frameRate);
  }
  if (!codec) {
    codec = CodecOptions[0].id;
    handleChangeValue('CreativeQualityAssuranceCodec', codec);
  }
  if (!audioBitrate) {
    audioBitrate = AudioBitrateOptions[0].id as string;
    handleChangeValue('CreativeQualityAssuranceAudioBitrate', audioBitrate);
  }
  if (!samplingFrequency) {
    samplingFrequency = SamplingFrequencyOptions[0].id as string;
    handleChangeValue('CreativeQualityAssuranceSamplingFrequency', samplingFrequency);
  }
  const handleChange = (fieldName: string, value: string): void => {
    handleChangeValue(fieldName, value);
  };

  return (
    <>
      {cqaEnabled && (
        <Rows noPadding gap={spacing.extraSmall} style={{ maxWidth: '450px' }}>
          <Row noPadding>
            <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Col>
                <Text extraSmall tKey='MinimumVideoResolution' />
              </Col>
              <Col>
                <IndependentSelectV2
                  handleApplySelect={(data: Set<string>) =>
                    handleChange(
                      'CreativeQualityAssuranceVideoResolution',
                      Array.from(data).join(''),
                    )
                  }
                  selectKey='Option'
                  labelTKey='Option'
                  options={VideoResolutionOptions}
                  selectedOptionIDs={new Set([videoResolution])}
                  key='CreativeQualityAssuranceVideoResolution'
                  maxWidth={200}
                  popoverWidth={280}
                  disableSearch
                />
              </Col>
            </Cols>
          </Row>
          <Row noPadding>
            <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Col>
                <Text extraSmall tKey='MinimumVideoBitrate' />
              </Col>
              <Col>
                <IndependentSelectV2
                  handleApplySelect={(data: Set<string>) =>
                    handleChange('CreativeQualityAssuranceVideoBitrate', Array.from(data).join(''))
                  }
                  selectKey='Option'
                  labelTKey='Option'
                  options={VideoBitrateOptions}
                  selectedOptionIDs={new Set([videoBitrate])}
                  key='CreativeQualityAssuranceVideoBitrate'
                  maxWidth={200}
                  popoverWidth={280}
                  disableSearch
                />
              </Col>
            </Cols>
          </Row>
          <Row noPadding>
            <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Col>
                <Text extraSmall tKey='VideoFormat' />
              </Col>
              <Col>
                <IndependentSelectV2
                  handleApplySelect={(data: Set<string>) =>
                    handleChange('CreativeQualityAssuranceVideoFormat', Array.from(data).join(''))
                  }
                  selectKey='Option'
                  labelTKey='Option'
                  options={VideoFormatOptions}
                  selectedOptionIDs={new Set([videoFormat])}
                  key='CreativeQualityAssuranceVideoFormat'
                  maxWidth={200}
                  popoverWidth={280}
                  disableSearch
                />
              </Col>
            </Cols>
          </Row>

          <Row noPadding>
            <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Col>
                <Text extraSmall tKey='MinimumFrameRate' />
              </Col>
              <Col>
                <IndependentSelectV2
                  handleApplySelect={(data: Set<string>) =>
                    handleChange('CreativeQualityAssuranceFrameRate', Array.from(data).join(''))
                  }
                  selectKey='Option'
                  labelTKey='Option'
                  options={FrameRateOptions}
                  selectedOptionIDs={new Set([frameRate])}
                  key='CreativeQualityAssuranceFrameRate'
                  maxWidth={200}
                  popoverWidth={280}
                  disableSearch
                />
              </Col>
            </Cols>
          </Row>
          <Row noPadding>
            <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Col>
                <Text extraSmall tKey='Codec' />
              </Col>
              <Col>
                <IndependentSelectV2
                  handleApplySelect={(data: Set<string>) =>
                    handleChange('CreativeQualityAssuranceCodec', Array.from(data).join(''))
                  }
                  selectKey='Option'
                  labelTKey='Option'
                  options={CodecOptions}
                  selectedOptionIDs={new Set([codec])}
                  key='CreativeQualityAssuranceCodec'
                  maxWidth={200}
                  popoverWidth={280}
                  disableSearch
                />
              </Col>
            </Cols>
          </Row>
          <Row noPadding>
            <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Col>
                <Text extraSmall tKey='MinimumAudioBitrate' />
              </Col>
              <Col>
                <IndependentSelectV2
                  handleApplySelect={(data: Set<string>) =>
                    handleChange('CreativeQualityAssuranceAudioBitrate', Array.from(data).join(''))
                  }
                  selectKey='Option'
                  labelTKey='Option'
                  options={AudioBitrateOptions}
                  selectedOptionIDs={new Set([audioBitrate])}
                  key='CreativeQualityAssuranceAudioBitrate'
                  maxWidth={200}
                  popoverWidth={280}
                  disableSearch
                />
              </Col>
            </Cols>
          </Row>
          <Row noPadding>
            <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Col>
                <Text extraSmall tKey='MinimumSamplingFrequency' />
              </Col>
              <Col>
                <IndependentSelectV2
                  handleApplySelect={(data: Set<string>) =>
                    handleChange(
                      'CreativeQualityAssuranceSamplingFrequency',
                      Array.from(data).join(''),
                    )
                  }
                  selectKey='Option'
                  labelTKey='Option'
                  options={SamplingFrequencyOptions}
                  selectedOptionIDs={new Set([samplingFrequency])}
                  key='CreativeQualityAssuranceSamplingFrequency'
                  maxWidth={200}
                  popoverWidth={280}
                  disableSearch
                />
              </Col>
            </Cols>
          </Row>
        </Rows>
      )}
    </>
  );
};
